import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Switch,
  Button,
  CircularProgress,
  Divider,
  Alert,
  useTheme,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  SystemUpdate as SystemUpdateIcon,
  LocalOffer as LocalOfferIcon,
  Campaign as CampaignIcon,
  Info as InfoIcon,
  Save as SaveIcon,
  NotificationsActive as NotificationsActiveIcon,
  AccountCircle as AccountCircleIcon,
  Newspaper as NewspaperIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { notificationService } from '../services/api/notificationService';

const NotificationSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: false,
    smsNotifications: false,
    pushNotifications: false,
    emailCategories: {
      dailyDigest: false,
      marketing: false,
      security: false
    },
    smsCategories: {
      marketing: false,
      security: false
    },
    notificationCategories: {
      messages: false,
      activity: false,
      systemUpdates: false,
      promotions: false,
      newsAndAnnouncements: false,
      accountActivity: false
    }
  });

  useEffect(() => {
    fetchNotificationSettings();
  }, []);

  const fetchNotificationSettings = async () => {
    try {
      setIsLoading(true);
      console.log("Bildirim ayarları alınıyor...");
      const response = await notificationService.getNotificationSettings();
      console.log("Bildirim ayarları yanıtı:", response);
      
      if (response.statusCode === 200 && response.data) {
        console.log("Ayarlanacak veri:", response.data);
        setSettings(response.data);
        console.log("Ayarlar state'i güncellendi:", response.data);
      } else {
        console.warn("Geçersiz bildirim ayarları yanıtı:", response);
        toast.error(t('notificationSettings.fetchError'));
      }
    } catch (error) {
      console.error('Bildirim ayarları alınırken hata:', error);
      toast.error(t('notificationSettings.fetchError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleEmailNotifications = (event) => {
    setSettings(prev => ({ ...prev, emailNotifications: event.target.checked }));
  };

  const handleToggleSmsNotifications = (event) => {
    setSettings(prev => ({ ...prev, smsNotifications: event.target.checked }));
  };

  const handleTogglePushNotifications = (event) => {
    setSettings(prev => ({ ...prev, pushNotifications: event.target.checked }));
  };

  const handleToggleCategoryNotification = (category, event) => {
    setSettings(prev => {
      const currentCategories = prev.notificationCategories || {
        messages: false,
        systemUpdates: false,
        promotions: false,
        newsAndAnnouncements: false
      };
      
      return {
        ...prev,
        notificationCategories: {
          ...currentCategories,
          [category]: event.target.checked
        }
      };
    });
  };

  const handleToggleEmailCategory = (category, event) => {
    setSettings(prev => ({
      ...prev,
      emailCategories: {
        ...prev.emailCategories,
        [category]: event.target.checked
      }
    }));
  };

  const handleToggleSmsCategory = (category, event) => {
    setSettings(prev => ({
      ...prev,
      smsCategories: {
        ...prev.smsCategories,
        [category]: event.target.checked
      }
    }));
  };

  const handleSaveAll = async () => {
    try {
      setIsSaving(true);
      console.log("Tüm ayarlar kaydediliyor:", settings);
      
      const response = await notificationService.updateNotificationSettings(settings);
      console.log("Ayarlar kaydetme cevabı:", response);
      
      if (response.statusCode === 200) {
        toast.success(t('notificationSettings.saveSuccess'));
      } else {
        toast.error(t('notificationSettings.saveError'));
      }
    } catch (error) {
      console.error('Bildirim ayarları kaydedilirken hata:', error);
      toast.error(t('notificationSettings.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const renderSettingItem = (icon, title, description, value, onChange) => {
    return (
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 2,
          borderRadius: 2,
          boxShadow: theme.shadows[1]
        }}
      >
        <CardContent>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              {icon}
            </Grid>
            <Grid item xs>
              <Typography variant="subtitle1" fontWeight="medium">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  mt: 0.5, 
                  display: 'block',
                  color: value ? theme.palette.success.main : theme.palette.text.secondary,
                  fontWeight: value ? 'bold' : 'normal'
                }}
              >
                {value ? t('common.allowed') : t('common.notAllowed')}
              </Typography>
            </Grid>
            <Grid item>
              <Switch
                checked={value}
                onChange={onChange}
                color="primary"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: theme.palette.background.default
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        px: 2,
        background: theme.palette.background.default
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            {t('notificationSettings.title')}
          </Typography>

          <Alert 
            severity="info" 
            icon={<InfoIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {t('notificationSettings.channelsDescription')}
          </Alert>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t('notificationSettings.notificationChannels')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('notificationSettings.channelsDescription')}
            </Typography>

            {renderSettingItem(
              <EmailIcon color="primary" />,
              t('notificationSettings.emailNotifications'),
              t('notificationSettings.emailDescription'),
              settings.emailNotifications,
              handleToggleEmailNotifications
            )}

            {settings.emailNotifications && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, ml: 2 }}>
                  {t('notificationSettings.emailCategories')}
                </Typography>

                {renderSettingItem(
                  <NewspaperIcon color="primary" />,
                  t('notificationSettings.emailDailyDigest'),
                  t('notificationSettings.emailDailyDigestDescription'),
                  settings.emailCategories?.dailyDigest || false,
                  (e) => handleToggleEmailCategory('dailyDigest', e)
                )}

                {renderSettingItem(
                  <LocalOfferIcon color="primary" />,
                  t('notificationSettings.emailMarketing'),
                  t('notificationSettings.emailMarketingDescription'),
                  settings.emailCategories?.marketing || false,
                  (e) => handleToggleEmailCategory('marketing', e)
                )}

                {renderSettingItem(
                  <SecurityIcon color="primary" />,
                  t('notificationSettings.emailSecurity'),
                  t('notificationSettings.emailSecurityDescription'),
                  settings.emailCategories?.security || false,
                  (e) => handleToggleEmailCategory('security', e)
                )}
              </>
            )}

            {renderSettingItem(
              <SmsIcon color="primary" />,
              t('notificationSettings.smsNotifications'),
              t('notificationSettings.smsDescription'),
              settings.smsNotifications,
              handleToggleSmsNotifications
            )}

            {settings.smsNotifications && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, ml: 2 }}>
                  {t('notificationSettings.smsCategories')}
                </Typography>

                {renderSettingItem(
                  <LocalOfferIcon color="primary" />,
                  t('notificationSettings.smsMarketing'),
                  t('notificationSettings.smsMarketingDescription'),
                  settings.smsCategories?.marketing || false,
                  (e) => handleToggleSmsCategory('marketing', e)
                )}

                {renderSettingItem(
                  <SecurityIcon color="primary" />,
                  t('notificationSettings.smsSecurity'),
                  t('notificationSettings.smsSecurityDescription'),
                  settings.smsCategories?.security || false,
                  (e) => handleToggleSmsCategory('security', e)
                )}
              </>
            )}

            {renderSettingItem(
              <NotificationsIcon color="primary" />,
              t('notificationSettings.pushNotifications'),
              t('notificationSettings.pushDescription'),
              settings.pushNotifications,
              handleTogglePushNotifications
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t('notificationSettings.notificationCategories')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('notificationSettings.categoriesDescription')}
            </Typography>

            {renderSettingItem(
              <ChatIcon color="primary" />,
              t('notificationSettings.messages'),
              t('notificationSettings.messagesDescription'),
              settings.notificationCategories?.messages || false,
              (e) => handleToggleCategoryNotification('messages', e)
            )}

            {renderSettingItem(
              <NotificationsActiveIcon color="primary" />,
              t('notificationSettings.activity'),
              t('notificationSettings.activityDescription'),
              settings.notificationCategories?.activity || false,
              (e) => handleToggleCategoryNotification('activity', e)
            )}

            {renderSettingItem(
              <SystemUpdateIcon color="primary" />,
              t('notificationSettings.systemUpdates'),
              t('notificationSettings.systemUpdatesDescription'),
              settings.notificationCategories?.systemUpdates || false,
              (e) => handleToggleCategoryNotification('systemUpdates', e)
            )}

            {renderSettingItem(
              <LocalOfferIcon color="primary" />,
              t('notificationSettings.promotions'),
              t('notificationSettings.promotionsDescription'),
              settings.notificationCategories?.promotions || false,
              (e) => handleToggleCategoryNotification('promotions', e)
            )}

            {renderSettingItem(
              <CampaignIcon color="primary" />,
              t('notificationSettings.newsAndAnnouncements'),
              t('notificationSettings.newsAndAnnouncementsDescription'),
              settings.notificationCategories?.newsAndAnnouncements || false,
              (e) => handleToggleCategoryNotification('newsAndAnnouncements', e)
            )}

            {renderSettingItem(
              <AccountCircleIcon color="primary" />,
              t('notificationSettings.accountActivity'),
              t('notificationSettings.accountActivityDescription'),
              settings.notificationCategories?.accountActivity || false,
              (e) => handleToggleCategoryNotification('accountActivity', e)
            )}
          </Box>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={isSaving}
            >
              {t('common.back')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSaveAll}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {isSaving ? t('common.loading') : t('notificationSettings.saveAll')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default NotificationSettings; 